import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Gate for page-scoped routes, decided by the SERVER's answer about this
 * session — `GET /api/auth/me/permissions`, which reads the very
 * `role_permissions` rows `requirePermission` reads on every API call. Layer
 * it inside ProtectedRoute:
 *
 *   <Route element={<ProtectedRoute />}>
 *     <Route element={<PermissionRoute page="role-list" />}>
 *       <Route path="/role-list" element={<RoleListPage />} />
 *     </Route>
 *   </Route>
 *
 * This REPLACES the old <RoleRoute roles={ROUTE_ROLES[path]} />, whose static
 * role arrays could not follow a runtime edit of the matrix: a role could be
 * offered a page the API would refuse, or denied one the API would now allow.
 *
 * IT IS STILL NOT THE GATE. Nothing here grants access — the server checks
 * every request regardless, and if the two disagree the server wins. What this
 * removes is the *divergence*, so the client stops offering and blocking on
 * stale information.
 *
 * THREE STATES, NEVER TWO:
 *   - known + granted   → render the page.
 *   - known + not granted → /403, exactly as before.
 *   - NOT KNOWN         → never guess. While loading, ProtectedRoute above is
 *     already holding the subtree on a spinner. If the read actually failed,
 *     say so and offer a retry, rather than bouncing the user to /403 (which
 *     would claim the server refused them, when it did not) or letting them in
 *     (which would strand them on a page of 403s).
 */
export default function PermissionRoute({ page, capability = 'view' }) {
  const { can, capabilitiesKnown, capabilitiesLoading, capabilitiesError, refreshCapabilities } = useAuth();
  const location = useLocation();

  if (capabilitiesLoading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading your permissions…</span>
        </div>
      </div>
    );
  }

  if (!capabilitiesKnown) {
    return (
      <div className="container py-5">
        <div className="alert alert-warning" role="alert">
          <h2 className="h5 alert-heading">We could not check your permissions</h2>
          <p className="mb-3">
            {capabilitiesError?.message ||
              'The server did not answer when we asked what this account may open.'}{' '}
            Until it does, this page cannot be opened — your access is decided by the server, and we will not
            guess at it.
          </p>
          <button type="button" className="btn btn-sm btn-outline-dark" onClick={() => refreshCapabilities()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!can(page, capability)) {
    return <Navigate to="/403" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
